import { useCallback, useEffect, useRef, useState } from "react";

// Plain scroll anchor for the chat surfaces. It scrolls to the bottom ONCE on
// mount when `initialToBottom` is set (so opening an existing conversation lands
// on the latest turn) and exposes a manual `scrollToBottom` + `isAtBottom` for
// the floating jump button — but it deliberately NEVER follows streaming
// content. This replaces `use-stick-to-bottom`, whose resize auto-stick kept
// scrolling the page while the LLM was still writing its answer.
//
// `scrollRef` goes to ScrollableLayout's scroll element; `onScroll` must be
// wired to ScrollableLayout's `onScroll` prop on surfaces that show the jump
// button (it is what keeps `isAtBottom` fresh).
export function useChatScrollAnchor(opts: { initialToBottom: boolean }) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Default true: a fresh/short transcript sits at the bottom, so the jump
  // button stays hidden until the user actually scrolls up.
  const [isAtBottom, setIsAtBottom] = useState(true);

  const scrollToBottom = useCallback((): void => {
    const el = scrollRef.current;
    if (el === null) return;
    el.scrollTo({ top: el.scrollHeight });
  }, []);

  // 24px tolerance so the button hides when effectively at the bottom.
  const onScroll = useCallback((): void => {
    const el = scrollRef.current;
    if (el === null) return;
    setIsAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 24);
  }, []);

  const initialToBottom = opts.initialToBottom;
  useEffect(() => {
    if (!initialToBottom) return;
    const el = scrollRef.current;
    if (el === null) return;
    el.scrollTo({ top: el.scrollHeight });
  }, [initialToBottom]);

  return { scrollRef, isAtBottom, scrollToBottom, onScroll };
}
